import { useQuery } from "@tanstack/react-query";
import { prequalificationApi } from "@/api/prequalification";
import type { PrequalAnswerDisplayItem } from "@/portal/api/prequalSite";
import { EntityRecordDrawer } from "@/components/layout/EntityRecordDrawer";
import { Skeleton } from "@/design-system/components/ui";
import { prequalKeys } from "@/hooks/prequalification/prequalKeys";
import { InlineQueryError } from "../queryStates";

/**
 * The answers behind one completed prequalification, read WITHOUT leaving the
 * buyer.
 *
 * <p>The row in "Prequalifications completed" carried an outcome and a dollar
 * figure and nothing behind either: a specialist defending the number to a
 * seller had no way to see what produced it. The whole row is the door now, and
 * this is what opens.</p>
 *
 * <p>🔴 <b>Every row is rendered by the SERVER's field registry — the same one
 * the broker-facing drawer uses.</b> The client does no formatting, no tier
 * logic and no conditional wording: a figure re-derived here is a figure that
 * can disagree with the Letter the applicant is already holding, and two
 * explanations of one field is two places they can drift. The five raw JSON
 * columns never cross the wire.</p>
 *
 * <p><b>Discloses nothing new.</b> The endpoint carries the same
 * <c>Prequalification.Read</c> gate as the full-page detail, whose payload
 * already returns those columns verbatim to the same caller. F1 and F2 both
 * hold that permission, so it works for the specialists it exists for; a caller
 * without it gets the permission-shaped state below rather than a broken
 * one.</p>
 */
export function PrequalificationAnswersDrawer({
  submissionId,
  title,
  subtitle,
  onClose,
}: {
  /** Null closes the drawer. The caller owns which row is open. */
  submissionId: string | null;
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  const open = Boolean(submissionId);

  const answers = useQuery({
    queryKey: prequalKeys.answers(submissionId ?? ""),
    queryFn: () => prequalificationApi.answers(submissionId!),
    // Lazily, when the drawer opens — never per row on render. A completed
    // prequalification is immutable, so nothing here needs to be re-read.
    enabled: open,
  });

  const rows = answers.data ?? [];

  return (
    <EntityRecordDrawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      // The way out to every write this drawer does not offer — assigning,
      // converting, archiving all live on the record's own page.
      fullPageHref={`/deal-flow/prequalification/${submissionId ?? ""}`}
      testId="prequal-answers-drawer"
    >
      {answers.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : answers.isError ? (
        /* Not "no answers recorded" — that is a claim about the submission, and
           the wrong one to make on a failed read. It would invite a specialist
           to tell a seller the engine had nothing to go on. */
        <InlineQueryError
          what="the prequalification answers"
          onRetry={() => void answers.refetch()}
          retrying={answers.isFetching}
        />
      ) : rows.length === 0 ? (
        <p className="m-0 text-caption text-gray-mid">
          This prequalification has no recorded answers. Its submission may have
          been purged on the retention clock.
        </p>
      ) : (
        <AnswerGroups rows={rows} />
      )}
    </EntityRecordDrawer>
  );
}

/**
 * The rows, in the server's own section order.
 *
 * <p>🔴 <b>An unrecognized group is SURFACED, not dropped.</b> The registry
 * stamps a group onto every row and this renders by that name; a row whose
 * group is not in the known order would otherwise vanish silently, which is the
 * one failure a reader cannot detect. It renders last, under its own raw name,
 * so a new server-side section shows up as something to name rather than as
 * missing answers.</p>
 */
function AnswerGroups({ rows }: { rows: PrequalAnswerDisplayItem[] }) {
  // Mirrors `PrequalAnswerGroups.Order`. Documents is omitted deliberately: the
  // registry explains answers, and this drawer requests answers only — the
  // generated artifacts live on the full page.
  const order = ["About", "Reported", "Calculated", "Readiness", "Screening"];
  const seen = new Set(order);
  const extras = rows.map((r) => r.group).filter((g) => !seen.has(g));
  const sections = [...order, ...new Set(extras)];

  return (
    <div className="grid gap-space-5" data-testid="prequal-answer-groups">
      {sections.map((group) => {
        const inGroup = rows.filter((r) => r.group === group);
        if (inGroup.length === 0) return null;
        return (
          <section key={group}>
            <h3 className="font-display text-h3 text-onyx mb-space-2">{group}</h3>
            <dl className="grid gap-space-3">
              {inGroup.map((row) => (
                <div key={row.fieldKey} data-testid="prequal-answer-row">
                  <dt className="text-caption uppercase tracking-wide text-gray-mid">
                    {row.label}
                  </dt>
                  <dd className="m-0 text-body text-onyx">{row.displayValue}</dd>
                  {row.explanation && (
                    /* Chosen by the registry from the VALUE — a coverage figure
                       above the sizing standard and one below it mean different
                       things, and the client is not the place that decides
                       which sentence applies. */
                    <dd className="m-0 mt-space-1 text-caption text-gray-mid">
                      {row.explanation}
                    </dd>
                  )}
                </div>
              ))}
            </dl>
          </section>
        );
      })}
    </div>
  );
}
